import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApolloClient, useMutation } from '@apollo/client';
import { Alert, alpha, Box, Button, ButtonBase, CircularProgress, TextField, Typography } from '@mui/material';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';

import { useToast } from '../../../common/hooks/useToast';
import { CrmPageFrame } from '../CrmPageFrame';
import { CRM_PATHS, workflowPath } from '../crmPaths';
import { StepIcon } from './StepIcon';
import { WorkflowBuilder } from './WorkflowBuilder';
import {
  DEFAULT_SETTINGS,
  describeWorkflowError,
  LIMITS,
  newKey,
  settingsInput,
  settingsProblems,
  StepDraft,
  stepOfError,
  toStepInput,
  workflowProblems,
  WorkflowSettings,
} from './workflowModel';
import { CRM_CREATE_WORKFLOW } from './workflows.queries';

/** Starting points: the shapes NationBuilder's automations mostly took. Ids are chosen after. */
const TEMPLATES: Array<{ name: string; hint: string; steps: () => Array<StepDraft> }> = [
  {
    name: 'Welcome series',
    hint: 'Tag them, an email the next day, then a call',
    steps: () => [
      { key: newKey(), type: 'ADD_TAG' },
      { key: newKey(), type: 'DELAY', delayMinutes: 1440 },
      { key: newKey(), type: 'SEND_EMAIL' },
      { key: newKey(), type: 'DELAY', delayUntilDow: 2, delayUntilHour: 10, delayMinutes: null },
      { key: newKey(), type: 'CREATE_CALL_TASK', dueInHours: 48, note: 'Welcome call: ask what brought them in' },
    ],
  },
  {
    name: 'Re-engage',
    hint: 'An email, a text a few days on, then a call',
    steps: () => [
      { key: newKey(), type: 'SEND_EMAIL' },
      { key: newKey(), type: 'DELAY', delayMinutes: 3 * 1440 },
      { key: newKey(), type: 'SEND_SMS', message: 'Hi {{ recipient.first_name_or_friend }}, we miss you. Reply STOP to opt out.' },
      { key: newKey(), type: 'DELAY', delayMinutes: 7 * 1440 },
      { key: newKey(), type: 'CREATE_CALL_TASK', dueInHours: 24, note: 'Check in: did they see our messages?' },
    ],
  },
  {
    name: 'Tag and call',
    hint: 'A tag, and a follow-up in the call queue',
    steps: () => [
      { key: newKey(), type: 'ADD_TAG' },
      { key: newKey(), type: 'CREATE_CALL_TASK', dueInHours: 0 },
    ],
  },
];

/** Building a workflow: the trigger, the steps in order, the guards. Saved as a draft; started from its page. */
export const CrmWorkflowNew = () => {
  const navigate = useNavigate();
  const client = useApolloClient();
  const toast = useToast();

  const [settings, setSettings] = useState<WorkflowSettings>(DEFAULT_SETTINGS);
  const [steps, setSteps] = useState<Array<StepDraft>>([]);
  const [tried, setTried] = useState(false);
  const [create, { loading, error }] = useMutation(CRM_CREATE_WORKFLOW);

  const problems = workflowProblems(steps);
  const settingErrors = settingsProblems(settings);
  const serverStep = stepOfError(error);
  if (serverStep !== null && !problems.has(serverStep)) problems.set(serverStep, describeWorkflowError(error));

  const save = async () => {
    setTried(true);
    if (problems.size > 0 || Object.keys(settingErrors).length > 0) return;
    try {
      const { data } = await create({ variables: { input: settingsInput(settings), steps: steps.map(toStepInput) } });
      const made = data.crmCreateWorkflow;
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'crmWorkflows' });
      client.cache.gc();
      toast.success(`Saved ${made.name} as a draft. Simulate it, then start it.`);
      navigate(workflowPath(made.id), { replace: true });
    } catch {
      // Shown from `error`.
    }
  };

  const blocking = tried ? [...(problems.get(-1) ? [problems.get(-1) as string] : []), ...Object.values(settingErrors)] : [];
  const stepIssues = tried ? Array.from(problems.keys()).filter((k) => k >= 0).length : 0;

  return (
    <CrmPageFrame
      title="New workflow"
      crumbs={[{ label: 'Workflows', to: CRM_PATHS.workflows }, { label: 'New workflow' }]}
      actions={
        <Button
          variant="contained"
          onClick={save}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SaveOutlinedIcon />}
        >
          Save draft
        </Button>
      }
    >
      <Box sx={{ display: 'grid', gap: 2 }}>
        <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) minmax(0, 1.4fr)' } }}>
          <TextField
            label="Name"
            value={settings.name}
            onChange={(event) => setSettings({ ...settings, name: event.target.value })}
            error={tried && !!settingErrors.name}
            helperText={tried ? settingErrors.name : 'Unique: no two workflows share a name.'}
            inputProps={{ maxLength: LIMITS.name }}
            autoFocus
          />
          <TextField
            label="What it is for (optional)"
            value={settings.description}
            onChange={(event) => setSettings({ ...settings, description: event.target.value })}
            inputProps={{ maxLength: LIMITS.description }}
          />
        </Box>

        {error && serverStep === null && <Alert severity="error">{describeWorkflowError(error, 'The workflow could not be saved.')}</Alert>}
        {(blocking.length > 0 || stepIssues > 0) && (
          <Alert severity="warning">
            {[...blocking, ...(stepIssues ? [`${stepIssues} step${stepIssues === 1 ? ' needs' : 's need'} attention: they are marked in red.`] : [])].join(' ')}
          </Alert>
        )}

        <WorkflowBuilder
          settings={settings}
          onSettings={setSettings}
          steps={steps}
          onSteps={setSteps}
          stepsEditable
          settingsEditable
          problems={tried ? problems : new Map()}
          showErrors={tried}
          aside={<Guide empty={steps.length === 0} onTemplate={(make) => setSteps(make())} />}
        />
      </Box>
    </CrmPageFrame>
  );
};

const Guide = ({ empty, onTemplate }: { empty: boolean; onTemplate: (make: () => Array<StepDraft>) => void }) => (
  <Box sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', p: 2, display: 'grid', gap: 1.5 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <AutoAwesomeOutlinedIcon sx={{ color: 'primary.main', fontSize: 20 }} />
      <Typography sx={{ fontWeight: 650 }}>{empty ? 'Start from a shape' : 'Replace with a shape'}</Typography>
    </Box>
    {TEMPLATES.map((t) => (
      <ButtonBase
        key={t.name}
        onClick={() => onTemplate(t.steps)}
        sx={{
          justifyContent: 'flex-start',
          textAlign: 'left',
          display: 'block',
          p: 1.25,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          transition: 'all 120ms',
          '&:hover': { borderColor: 'primary.main', bgcolor: alpha('#4f46e5', 0.04) },
        }}
      >
        <Typography sx={{ fontWeight: 600, fontSize: '0.85rem' }}>{t.name}</Typography>
        <Typography variant="caption" color="text.secondary" component="div">
          {t.hint}
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5, mt: 0.75, color: 'text.secondary' }}>
          {t.steps().map((s) => (
            <StepIcon key={s.key} type={s.type} fontSize={16} />
          ))}
        </Box>
      </ButtonBase>
    ))}
    <Typography variant="caption" color="text.secondary" component="div" sx={{ lineHeight: 1.5 }}>
      Click the trigger or a step to change it. It is saved as a draft: nothing runs until you simulate it and press Start on its page. Consent is checked at
      every send, so someone who unsubscribes part-way gets nothing more.
    </Typography>
  </Box>
);
